import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/LeadCard.css';
import { Image, X } from '../assets/icons';
import CustomSwiper from './CustomSwiper';
import ThirdPartyEstimates from './ThirdPartyEstimates';
import { formatterCompact, numberFormatter, colour } from '../styles/GlobalStyles';
import { getImageFolderProvider, isUnsupportedImageProvider, prioritizePropertyPhotos, resizedImageUrl, upgradeRdcpixUrl } from '../utils/imageUtils';
import { getPropertyTypeLabel, normalizePropertyTypeKey } from '../utils/propertyTypeUtils';

// Additional safety filter beyond getImageFolderProvider /
// isUnsupportedImageProvider — staticmap URLs sneak in as enrichment
// fallbacks and always 403 in prod. Keep this narrow; the canonical
// provider checks above handle the rest.
const STATICMAP_PATTERN = /maps\.googleapis\.com\/maps\/api\/staticmap/i;
const isRenderableImageUrl = (url) =>
    typeof url === 'string' && url.length > 0 && !STATICMAP_PATTERN.test(url);

// Same shape as LeadsActivityRail's formatter — keeping it inline rather
// than exporting from there since the helper is 4 lines and a third
// caller doesn't yet justify a shared util.
function formatLeadAge(ms) {
    if (!ms) return '';
    const diff = Date.now() - ms;
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
}

// imageLimit caps how many <img> a single card mounts (the inline carousel
// eagerly decodes every slide it renders). The public feed renders every card
// unwindowed, so on mobile — where decoded-image memory OOM-crashes Safari and
// a card tap navigates to the detail page anyway — callers pass imageLimit={1}
// to mount a single cover instead of up to 8 slides (≈8× less decoded memory).
function LeadCard({ lead, isClicked, onClick, onOpenImages, navigateOnImageClick = false, imageLimit = 8, parkingVerdict = null, parkingWaived = false, verdictReason = null }) {
    // Hover-to-highlight was removed by request — only explicit clicks change
    // selection. Keeping the click handler is enough; the parent decides
    // whether the click selects (map pin) or navigates (card).
    const handleClick = useCallback(() => {
        if (onClick) onClick(lead);
    }, [onClick, lead]);

    const formatCurrencyCompact = (value) => {
        if (value === null || value === undefined || isNaN(value)) return '-';
        return formatterCompact.format(value);
    };

    const address = lead.address || {};
    const characteristics = lead.verifiedCharacteristics || {};
    // Land has no beds / baths / living area — those fields come through as 0 /
    // null and read as noise ("Land · 0bd · 0ba · — sqft"). Detect it once so the
    // meta row can drop the residential characteristics for land listings.
    const propertyTypeKey = normalizePropertyTypeKey(lead.propertyType);
    const isLand = propertyTypeKey === 'LAND';
    const showPropertyType = !!lead.propertyType && propertyTypeKey !== 'OTHER';
    // Map-view leads come through a cloud-function JSON boundary, so
    // Firestore Timestamps deserialize as `{_seconds, _nanoseconds}` rather
    // than the SDK's `{seconds}` / `.toMillis()` shape. Cover all three so
    // the age renders whether the lead came from getLeadsInBounds (HTTP),
    // leadsBootstrap (HTTP), or a live snapshot.
    const ts = lead.createdAt;
    const createdMs = ts?.toMillis?.()
        || (ts?.seconds ? ts.seconds * 1000 : null)
        || (ts?._seconds ? ts._seconds * 1000 : null)
        || (typeof ts === 'string' ? Date.parse(ts) || null : null);
    // Prefer lastSeenMs (last-pickup epoch-ms, bumped on every X re-post) so the
    // card's age matches the recency filter, which also keys on lastSeenAt: a
    // re-blasted deal that re-enters the "Last 24h" window reads "Nh ago" rather
    // than its original post age. lastSeenMs is only set on the X feeds
    // (Spencer's / Trial); other callers (Leads) fall back to createdAt unchanged.
    const ageMs = lead.lastSeenMs > 0 ? lead.lastSeenMs : createdMs;
    const ageLabel = ageMs ? formatLeadAge(ageMs) : '';
    const photoUrls = lead.photoUrls || {};

    // Mirrors LeadDetailV2's image resolution (see LeadDetailV2.js:817)
    // so the card thumbnail matches the detail-page hero. Order:
    //   1. repairCostImageUrls — highest-quality Firebase Storage URLs
    //      from V3 repair-cost analysis
    //   2. lead.imageUrls — operator-uploaded, filtered through the
    //      canonical provider checks (no Drive/Dropbox/OneDrive folders,
    //      no Google Photos hosts the backend can't fetch from)
    //   3. photoUrls.{zillow,redfin,realtor} — enrichment thumbnails
    // Then re-rank by lead.photoOrder when present so the operator's
    // saved photo order wins. Same merge LeadDetailV2 runs, minus its
    // imageLabels branch (classifier mirror is detail-only).
    const galleryImages = useMemo(() => {
        const repairImages = (lead.repairCostImageUrls || []).filter(isRenderableImageUrl);
        const leadImages = (lead.imageUrls || []).filter(url =>
            url && !isUnsupportedImageProvider(url) && !getImageFolderProvider(url) && isRenderableImageUrl(url)
        );
        const enrichmentThumbs = [photoUrls.zillow, photoUrls.redfin, photoUrls.realtor]
            .filter(url => url && typeof url === 'string' && url.trim().length > 0 && isRenderableImageUrl(url));

        const seen = new Set();
        // Upgrade bare Realtor thumbnails to full size BEFORE deduping — the two
        // arrays can carry the same photo at different sizes, so only a shared
        // normal form collapses them (otherwise the card lists each twice).
        // Matches deriveDisplayImageUrls in utils/imageUtils.js.
        const displaySet = [...repairImages, ...leadImages, ...enrichmentThumbs]
            .map(upgradeRdcpixUrl)
            .filter(url => {
                if (!url || seen.has(url)) return false;
                seen.add(url);
                return true;
            });

        if (Array.isArray(lead.photoOrder) && lead.photoOrder.length > 0) {
            const inDisplay = new Set(displaySet);
            const orderedSeen = new Set();
            const ordered = [];
            for (const raw of lead.photoOrder) {
                const url = upgradeRdcpixUrl(raw);
                if (inDisplay.has(url) && !orderedSeen.has(url)) {
                    orderedSeen.add(url);
                    ordered.push(url);
                }
            }
            const remaining = displaySet.filter(url => !orderedSeen.has(url));
            // respectOrder so an operator's chosen featured photo leads the card
            // hero too, instead of the folder-first default yanking it back.
            return prioritizePropertyPhotos([...ordered, ...remaining], { respectOrder: true });
        }
        return prioritizePropertyPhotos(displaySet, { featuredUrl: upgradeRdcpixUrl(lead.featuredImageUrl) });
    }, [lead.repairCostImageUrls, lead.imageUrls, photoUrls.zillow, photoUrls.redfin, photoUrls.realtor, lead.photoOrder, lead.featuredImageUrl]);

    const featuredImage = galleryImages[0] || null;
    const hasImages = galleryImages.length > 0;

    // Card display uses RESIZED variants (~640px ≈ 2 MB decoded) instead of the
    // full-res originals (~12 MB decoded each) so a feed of cards can't blow
    // mobile Safari's decoded-image budget. The fullscreen gallery
    // (onOpenImages) still receives the original galleryImages — only a few are
    // visible there at once. resizedImageUrl passes non-Storage URLs through
    // unchanged, so enrichment thumbs are unaffected.
    const cardImages = useMemo(() => galleryImages.map((u) => resizedImageUrl(u, 640)), [galleryImages]);
    const cardFeatured = cardImages[0] || null;

    // Track <img> load failures so we can swap the broken thumbnail for
    // the placeholder rather than leaving an empty box. Reset whenever
    // the featuredImage URL changes (a different lead, or photoOrder
    // moved a different photo to the front).
    const [imgError, setImgError] = useState(false);
    useEffect(() => { setImgError(false); }, [featuredImage]);
    const showPlaceholder = !featuredImage || imgError;

    // Image click — open the in-app ScrollableImages → ImageGallery flow
    // owned by the parent (Leads page). Same UX PropertyCard uses via
    // showScrollableImages. No-op when there are no images to show.
    // The event arg is optional: CustomSwiper calls imageClick() with no
    // args (propagation stopped by the swiper wrapper), while the single
    // <img> passes its event so the click doesn't bubble to the card-select
    // handler on the container.
    const openGallery = useCallback((e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        // On the public feeds (mobile), an image tap should route to the deal
        // detail page rather than open the in-app gallery — same target as a
        // card-body tap. Desktop keeps the gallery viewer.
        if (navigateOnImageClick) {
            if (onClick) onClick(lead);
            return;
        }
        if (hasImages && onOpenImages) {
            onOpenImages(lead, galleryImages);
        }
    }, [hasImages, onOpenImages, lead, galleryImages, navigateOnImageClick, onClick]);

    // Third-party estimate pills (Zillow / Redfin / Realtor). Reshape the
    // lead's flat estimates + sourceUrls into the {source: {estimate, url}}
    // shape ThirdPartyEstimates expects. innerWidth is forced past the
    // component's 1330 gate (same bypass LeadDetail.js uses).
    const estimates = lead.estimates || {};
    const sourceUrls = lead.sourceUrls || {};
    const propertyEstimates = useMemo(() => ({
        zillow: { estimate: estimates.zillow ?? null, url: sourceUrls.zillow ?? null },
        redfin: { estimate: estimates.redfin ?? null, url: sourceUrls.redfin ?? null },
        realtor: { estimate: estimates.realtor ?? null, url: sourceUrls.realtor ?? null },
    }), [estimates.zillow, estimates.redfin, estimates.realtor, sourceUrls.zillow, sourceUrls.redfin, sourceUrls.realtor]);
    const hasEstimates = estimates.zillow != null || estimates.redfin != null || estimates.realtor != null;

    const asking = (lead.currentAskingPrice != null && !isNaN(lead.currentAskingPrice)) ? lead.currentAskingPrice : null;

    return (
        <div
            className={`lead-card-container ${isClicked ? 'lead-card-clicked' : ''}`}
            onClick={handleClick}
        >
            {/* Featured Image — anchors the card visually. Multi-photo leads
                get the same inline CustomSwiper PropertyCard uses; clicking a
                slide opens the in-app gallery (ScrollableImages → ImageGallery).
                Only the interactive image content swallows the click (so it
                doesn't bubble to the card-select handler); the no-image
                placeholder bubbles so clicking it still selects the lead. */}
            <div className="lead-card-image-section">
                {/* Parking verdict overlay — only the public co-living feed passes
                    parkingVerdict, so other callers render no badge. Sits inside
                    the card (top-left of the hero) rather than above it. */}
                {(parkingVerdict === 'PASS' || parkingVerdict === 'FAIL') && (
                    <span
                        className={`lead-card-parking-badge label-bold-caps lead-card-parking-badge--${parkingVerdict === 'PASS' ? 'pass' : (parkingWaived ? 'waived' : 'fail')}`}
                    >
                        {/* A transit-waived FAIL is NOT "Parking Fail" — the deal survived,
                            and this badge is the only parking signal on the card. */}
                        {parkingVerdict === 'PASS' ? 'Parking Pass'
                            : parkingWaived ? 'Parking · Transit' : 'Parking Fail'}
                    </span>
                )}
                {showPlaceholder ? (
                    <div className="lead-card-image-placeholder">
                        <Image fill={colour.gray04} className="lead-card-placeholder-icon" />
                        <span className="xs-label colour-tertiary lead-card-placeholder-label">No image</span>
                    </div>
                ) : (galleryImages.length === 1 || imageLimit <= 1) ? (
                    <img
                        src={cardFeatured}
                        // Intentionally NO 2x srcset: a card is ~300-350px wide,
                        // but a 2x/3x variant (1280px ≈ 8.7 MB decoded) would
                        // reload on every retina phone — i.e. all mobile — and
                        // re-blow the decoded-image budget this fix exists to
                        // bound (worse with the unwindowed 150-cap rail). 640
                        // everywhere matches the swiper path; slight softness on
                        // retina is the deliberate trade for not crashing.
                        alt={address.streetAddress || 'Property'}
                        className="lead-card-image lead-card-image-clickable"
                        onClick={openGallery}
                        onError={() => setImgError(true)}
                        loading="lazy"
                        decoding="async"
                    />
                ) : (
                    <div
                        className="lead-card-swiper-wrap"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <CustomSwiper
                            images={cardImages}
                            // Card hero is a thumbnail carousel — cap the slides
                            // it mounts. The full gallery opens on click via
                            // onOpenImages (ScrollableImages → ImageGallery), so
                            // nothing is lost. Without a cap a feed that renders
                            // every card unwindowed (e.g. /spencers-deals) mounts
                            // up to 100 cards × 100 <img> each and OOM-crashes
                            // mobile Safari ("A problem repeatedly occurred").
                            // images are resized variants (~640px) — the original
                            // full-res set is reserved for the fullscreen gallery.
                            // imageLimit is caller-controlled (1 on the mobile feed).
                            imageLimit={imageLimit}
                            title={address.streetAddress || 'Property'}
                            arrowClicked={null}
                            imageClick={openGallery}
                            subjectProperty={false}
                        />
                    </div>
                )}
            </div>

            {/* Info Section: address → meta → price → third-party estimates.
                Reads top-down (where, what, how much), with estimate pills
                closing the stack below the price. No separate right-side
                column. Price is omitted entirely when there's no asking. */}
            <div className="lead-card-info-section">
                <div className="lead-card-address-row">
                    <span className="body-semibold colour-primary lead-card-address-line">
                        {address.streetAddress || '-'}
                    </span>
                    {(ageLabel || lead.postUrl) && (
                        <div className="lead-card-meta-right">
                            {ageLabel && (
                                <span className="label-regular colour-tertiary lead-card-age">
                                    {ageLabel}
                                </span>
                            )}
                            {lead.postUrl && (
                                <a
                                    href={lead.postUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="lead-card-post-link"
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="View original post on X"
                                >
                                    <X className="lead-card-post-link-icon" />
                                </a>
                            )}
                        </div>
                    )}
                </div>
                <span className="label-regular colour-secondary lead-card-city-line">
                    {[address.city, address.state].filter(Boolean).join(', ') || '-'}
                </span>
                <div className="lead-card-info-bottom">
                    {(() => {
                        // Build the meta segments, then interleave "·" separators
                        // so omitting any segment (e.g. beds/baths/sqft on land)
                        // never leaves a leading or dangling separator.
                        const segments = [];
                        if (showPropertyType) {
                            segments.push(
                                <span className="label-regular colour-secondary">
                                    {getPropertyTypeLabel(lead.propertyType)}
                                </span>
                            );
                        }
                        // Beds / baths / living area are meaningless for land —
                        // drop them so the row reads just "Land" (+ year if any).
                        if (!isLand) {
                            segments.push(
                                <span className="label-regular colour-secondary">
                                    {characteristics.bedrooms ?? '-'}bd
                                </span>
                            );
                            segments.push(
                                <span className="label-regular colour-secondary">
                                    {characteristics.bathrooms ?? '-'}ba
                                </span>
                            );
                            segments.push(
                                <span className="label-regular colour-secondary">
                                    {characteristics.livingSquareFeet ? `${numberFormatter.format(characteristics.livingSquareFeet)} sqft` : '— sqft'}
                                </span>
                            );
                        }
                        if (characteristics.yearBuilt != null && characteristics.yearBuilt > 0) {
                            segments.push(
                                <span className="label-regular colour-secondary">
                                    {characteristics.yearBuilt}
                                </span>
                            );
                        }
                        return segments.map((seg, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <span className="label-regular colour-tertiary">·</span>}
                                {seg}
                            </React.Fragment>
                        ));
                    })()}
                </div>
                {asking != null && (
                    <span className="body-semibold colour-primary lead-card-primary-metric">
                        {formatCurrencyCompact(asking)}
                    </span>
                )}
                {/* Why the verdict went the way it did, in the card's own words.
                    Same contract as parkingVerdict above — optional, null for
                    every caller that doesn't pass it, so no other feed changes.
                    It lives INSIDE the card deliberately: #1037 moved the verdict
                    in here and dropped the rail badges, and a reason rendered
                    outside would walk that back. The badge says PASS or FAIL; this
                    says what it cost. */}
                {verdictReason && (
                    <span className="xs-label colour-secondary lead-card-verdict-reason">
                        {verdictReason}
                    </span>
                )}
                {/* Third-party estimate pills (Zillow / Redfin / Realtor) sit
                    below the price. Clicking an estimate opens that source in
                    a new tab. */}
                {hasEstimates && (
                    <div className="lead-card-links-section" onClick={(e) => e.stopPropagation()}>
                        <ThirdPartyEstimates propertyEstimates={propertyEstimates} innerWidth={9999} small />
                    </div>
                )}
            </div>
        </div>
    );
}

export default React.memo(LeadCard);
